import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { BandSlotProvider, useBandSlotElement } from "../components/shell/band";
import { LanguageSwitch } from "../components/shell/LanguageSwitch";
import { Logo } from "../components/ui";
import { requireAuth, useMe, useSignOut } from "../features/auth/session";
import { useI18n } from "../i18n/I18nProvider";
import { cn } from "../lib/cn";

/** Everything under /app needs a session; the loader data is the /me response. */
export const Route = createFileRoute("/app")({
  beforeLoad: ({ context, location }) => requireAuth(context.queryClient, location.href),
  staticData: { appShell: true },
  component: AppLayout,
});

const NAV_LINK =
  "border-b-[3px] border-transparent pt-2.5 pb-3 font-semibold whitespace-nowrap text-band-foreground/75 no-underline hover:text-band-foreground hover:no-underline";
const NAV_ACTIVE = { className: "border-highlight! text-band-foreground!" };

function AppLayout() {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const me = useMe();
  const profileLocale = me.data?.user.locale;
  const [slot, setSlot] = useBandSlotElement();
  const wide = useRouterState({
    select: (state) => state.matches.some((match) => match.staticData.wideLayout === true),
  });
  const inSettings = useRouterState({
    select: (state) => state.location.pathname.startsWith("/app/settings"),
  });

  /**
   * Adopt the profile's language once, when it first arrives.
   *
   * It used to run on every change of either value, which made the language switch inert
   * inside the app: selecting Finnish set the state, this effect saw it differ from the
   * profile and set it straight back. A control that silently undoes itself is worse than no
   * control. The profile still decides what you get on arrival; changing it for good is the
   * profile setting, and the band's switch changes it for the session.
   */
  const adopted = useRef<string | null>(null);
  useEffect(() => {
    if (profileLocale === undefined || adopted.current === profileLocale) return;
    adopted.current = profileLocale;
    if (profileLocale !== locale) setLocale(profileLocale);
    // `locale` is deliberately not a dependency: this follows the profile, not the switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLocale, setLocale]);

  const data = me.data;
  const active = data?.memberships.find((m) => m.organizationId === data.activeOrganizationId);
  const manyWorkspaces = (data?.memberships.length ?? 0) > 1;

  return (
    <div className="flex min-h-full flex-col">
      {/* SP-032: the ultramarine band. Pages add their headline to its lower half (PageBand). */}
      <div className="bg-band text-band-foreground relative overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10">
          <header className="relative z-10 flex flex-wrap items-center gap-x-6 gap-y-3 py-4">
            <Link to="/app" className="text-band-foreground no-underline hover:no-underline">
              <Logo name={t("app.name")} />
            </Link>
            <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium">
              {/* The organization name is where you switch or add one: no tab for it. */}
              {active ? (
                <Link
                  to="/app/organizations"
                  data-testid="active-organization"
                  title={t("nav.organizations")}
                  className="border-band-foreground/40 text-band-foreground rounded-md border-[1.5px] px-3 py-1.5 no-underline hover:no-underline"
                >
                  {active.organizationName}
                </Link>
              ) : null}
              <LanguageSwitch onBand />
              {data?.user.isPlatformAdmin ? (
                <Link to="/app/admin" className="text-band-foreground">
                  {t("nav.admin")}
                </Link>
              ) : null}
              <button
                type="button"
                className="text-band-foreground cursor-pointer hover:underline"
                title={data?.user.email}
                onClick={() => {
                  void signOut().then(() => navigate({ to: "/login" }));
                }}
              >
                {t("nav.signOut")}
              </button>
            </div>
          </header>
          <nav
            aria-label={t("nav.main")}
            className="border-band-foreground/20 relative z-10 -mx-4 flex gap-7 overflow-x-auto border-b px-4 text-[0.9375rem] sm:mx-0 sm:px-0"
          >
            <Link
              to="/app"
              activeOptions={{ exact: true }}
              className={NAV_LINK}
              activeProps={NAV_ACTIVE}
            >
              {t("nav.dashboard")}
            </Link>
            <Link to="/app/campaigns" className={NAV_LINK} activeProps={NAV_ACTIVE}>
              {t("nav.campaigns")}
            </Link>
            <Link to="/app/insights" className={NAV_LINK} activeProps={NAV_ACTIVE}>
              {t("nav.insights")}
            </Link>
            <Link to="/app/audiences" className={NAV_LINK} activeProps={NAV_ACTIVE}>
              {t("nav.audiences")}
            </Link>
            <Link to="/app/ad-accounts" className={NAV_LINK} activeProps={NAV_ACTIVE}>
              {t("nav.adAccounts")}
            </Link>
            {/* Most customers have one organization; the tab only confuses them. */}
            {manyWorkspaces ? (
              <Link to="/app/organizations" className={NAV_LINK} activeProps={NAV_ACTIVE}>
                {t("nav.organizations")}
              </Link>
            ) : null}
            {/* Active on every settings tab, not only the profile it opens on. */}
            <Link
              to="/app/settings/profile"
              className={cn(NAV_LINK, inSettings && NAV_ACTIVE.className)}
            >
              {t("nav.settings")}
            </Link>
          </nav>
          {/* Not positioned: a PageBand's elephant anchors to the band itself. */}
          <div ref={setSlot} />
        </div>
      </div>
      <BandSlotProvider slot={slot}>
        <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
          <div className={cn(!wide && "max-w-3xl")}>
            <Outlet />
          </div>
        </main>
      </BandSlotProvider>
    </div>
  );
}
